import { useState } from 'react';
import { useGAFormTracking } from '../../../shared/hooks';
import { useWizard } from 'react-use-wizard';
import { useMatcherContext } from '../context';
import { getAnsweredString } from '../flow/types';
import { FieldErrors, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { matcherZipSchema } from '../schemas/MatcherZipSchema';
import {
  getMatcherFormName,
  MATCHER_NUMERIC_INPUT_ATTRS,
  useMatcherButtonSize,
  useMatcherFieldSize,
  useTrackMatcherStepStarted
} from '../utils';
import {
  trackStepCompleted,
  trackStepErrorViewed
} from '../../../shared/analytics';
import {
  MatcherProJoinOurProgram,
  MatcherStandardStepTitle,
  MatcherStepWrapper
} from '../components';
import FormField from '@ramsey-design-system/form-field';
import Input from '@ramsey-design-system/input';
import Button from '@ramsey-design-system/button';
import { ArrowRightIcon } from '@ramsey-design-system/icons';

const STEP_NAME = 'matcher_location_step';
const STEP_LABEL = 'Where are you searching for a pro?';

interface MatcherLocationStepFields {
  postal_code: string;
}

function MatcherLocationStep() {
  const formRef = useGAFormTracking('matcher_where_are_you_located_step');
  const { nextStep, activeStep } = useWizard();
  const { matcherAnswers, updateMatcherAnswer } = useMatcherContext();
  const fieldSize = useMatcherFieldSize();
  const buttonSize = useMatcherButtonSize();
  const {
    register,
    handleSubmit,
    formState: { errors }
  } = useForm<MatcherLocationStepFields>({
    defaultValues: {
      postal_code: getAnsweredString(matcherAnswers.postalCode)
    },
    resolver: yupResolver(matcherZipSchema)
  });
  const [isLoading, setIsLoading] = useState(false);

  useTrackMatcherStepStarted(activeStep + 1, STEP_NAME, STEP_LABEL);

  async function onValidSubmit(values: MatcherLocationStepFields) {
    setIsLoading(true);
    updateMatcherAnswer('postalCode', {
      status: 'answered',
      value: values.postal_code
    });
    trackStepCompleted({
      step: activeStep + 1,
      step_name: STEP_NAME,
      step_label: STEP_LABEL,
      answer_labels: ['Zip Code'],
      answer_values: [values.postal_code],
      form_name: getMatcherFormName()
    });
    await nextStep();
    setIsLoading(false);
  }

  function onInvalidSubmit(errors: FieldErrors<MatcherLocationStepFields>) {
    trackStepErrorViewed({
      step: activeStep + 1,
      step_name: STEP_NAME,
      step_label: STEP_LABEL,
      error_type: 'validation',
      error_field: 'postal_code',
      error_message: errors.postal_code?.message ?? '',
      form_name: getMatcherFormName()
    });
  }

  return (
    <MatcherStepWrapper>
      <form
        ref={formRef}
        onSubmit={handleSubmit(onValidSubmit, onInvalidSubmit)}
        noValidate
        className="flex-1 flex flex-col"
      >
        <MatcherStandardStepTitle noSubtitle>
          Where are you searching for a pro?
        </MatcherStandardStepTitle>

        <div className="mb-4">
          <FormField
            id="zip_code"
            label="Zip Code"
            size={fieldSize}
            invalid={!!errors.postal_code}
            errors={[errors.postal_code?.message]}
          >
            {() => (
              <Input
                {...register('postal_code')}
                className={
                  '!max-w-full placeholder:text-gray-50 placeholder:text-3 sm:placeholder:text-4'
                }
                size={fieldSize}
                invalid={!!errors.postal_code}
                id="zip_code"
                data-testid="tax_matcher_zip_code_input"
                autoComplete="postal-code"
                placeholder="e.g., 12345"
                {...MATCHER_NUMERIC_INPUT_ATTRS}
              />
            )}
          </FormField>
        </div>

        <Button
          data-testid="matcher_standard_next_button"
          type="submit"
          icon={isLoading ? undefined : ArrowRightIcon}
          iconPosition="right"
          className={'!w-full mt-auto md:mt-0'}
          fullWidthOnMobile={true}
          size={buttonSize}
          loading={isLoading}
        >
          {isLoading ? 'Loading' : 'Next'}
        </Button>
      </form>
      <MatcherProJoinOurProgram />
    </MatcherStepWrapper>
  );
}

export { MatcherLocationStep };
